import React from 'react';
import { Box, Typography, Container, Paper, Divider } from '@mui/material';
import { SiteHeader } from '../components/site-header';
import { SiteFooter } from '../components/site-footer';

const PrivacyPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col bg-[#E0EAF3]">
      <SiteHeader />
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Política de Privacidad
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" paragraph>
          Última actualización: {new Date().toLocaleDateString('es-ES')}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Introducción
        </Typography>

        <Typography variant="body1" paragraph>
          En JugarHoy ("nosotros", "nuestro" o "la aplicación"), respetamos su privacidad y nos comprometemos a proteger
          sus datos personales. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos y protegemos
          su información cuando utiliza nuestra aplicación móvil para reservar instalaciones deportivas.
        </Typography>

        <Typography variant="body1" paragraph>
          Al utilizar JugarHoy, usted acepta las prácticas descritas en esta Política de Privacidad.
          Si no está de acuerdo con esta política, le rogamos que no utilice nuestra aplicación.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          1. Información que Recopilamos
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>1.1 Información que usted nos proporciona directamente:</strong>
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Información de cuenta:</strong> Nombre completo, dirección de correo electrónico, número de teléfono,
            fotografía de perfil (opcional)
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Información deportiva:</strong> Deportes que practica, nivel de habilidad, preferencias de juego
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Información de reservas:</strong> Detalles de sus reservas, historial de uso de instalaciones,
            preferencias de horarios y ubicaciones
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Información de pago:</strong> JugarHoy NO procesa pagos directamente. Redirigimos a los usuarios
            a MercadoPago (procesador de pagos certificado) en nombre de los clubes. Los clubes pagan una tarifa
            mensual por contrato basada en la cantidad de servicios prestados cada mes. No almacenamos ni tenemos
            acceso a números de tarjetas de crédito o información bancaria sensible. Todos los datos de pago de
            usuarios son manejados exclusivamente por MercadoPago según su propia política de privacidad y seguridad.
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Información de club:</strong> Si es administrador de un club, información del club, instalaciones,
            precios y disponibilidad
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          <strong>1.2 Información recopilada automáticamente:</strong>
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Ubicación:</strong> Con su permiso, recopilamos datos de ubicación para mostrarle canchas cercanas
            y facilitar las reservas
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Información del dispositivo:</strong> Tipo de dispositivo, sistema operativo, identificador único del dispositivo,
            dirección IP, información del navegador
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Datos de uso:</strong> Páginas visitadas, funciones utilizadas, tiempo de uso, errores y fallos de la aplicación
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Notificaciones push:</strong> Token de notificaciones push si otorga permiso para recibir notificaciones
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          <strong>1.3 Información de terceros:</strong>
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Autenticación social:</strong> Si inicia sesión con Google o Apple, recibimos información básica de perfil
            (nombre, correo electrónico, foto de perfil) según los permisos que otorgue
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Invitaciones de clubes:</strong> Información proporcionada por administradores de clubes cuando lo agregan
            como miembro (permanece privada hasta que usted se registre)
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          2. Cómo Utilizamos su Información
        </Typography>

        <Typography variant="body1" paragraph>
          Utilizamos la información recopilada para los siguientes propósitos:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Proveer el servicio:</strong> Facilitar reservas de instalaciones deportivas, procesar pagos,
            gestionar su cuenta y preferencias
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Mejorar la experiencia:</strong> Personalizar recomendaciones de canchas, recordar sus preferencias,
            optimizar la búsqueda de instalaciones cercanas
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Comunicaciones:</strong> Enviar confirmaciones de reservas, recordatorios, notificaciones importantes
            sobre cambios en el servicio
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Soporte al cliente:</strong> Responder a sus consultas, resolver problemas técnicos,
            proporcionar asistencia con reservas
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Seguridad:</strong> Detectar y prevenir fraudes, proteger contra actividades maliciosas,
            garantizar la seguridad de la plataforma
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Análisis:</strong> Analizar patrones de uso, medir el rendimiento de la aplicación,
            realizar investigaciones y análisis para mejorar nuestros servicios
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cumplimiento legal:</strong> Cumplir con obligaciones legales, resolver disputas,
            hacer cumplir nuestros términos y condiciones
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          3. Cómo Compartimos su Información
        </Typography>

        <Typography variant="body1" paragraph>
          No vendemos ni alquilamos su información personal a terceros. Compartimos su información únicamente en las siguientes circunstancias:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Con clubes e instalaciones deportivas:</strong> Cuando realiza una reserva, compartimos su nombre
            y datos de contacto necesarios con la instalación para confirmar y gestionar su reserva
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Proveedores de servicios:</strong> Compartimos información con terceros que nos ayudan a operar la aplicación:
          </Typography>
        </Box>

        <Box component="ul" sx={{ pl: 6 }}>
          <Typography component="li" variant="body1">
            <strong>Firebase / Google Cloud:</strong> Autenticación, almacenamiento de datos, notificaciones push
          </Typography>
          <Typography component="li" variant="body1">
            <strong>MercadoPago:</strong> Redirigimos a los usuarios a MercadoPago para procesar pagos de forma segura
            en nombre de los clubes. MercadoPago maneja todos los datos de pago según su propia política de privacidad.
            Recibimos únicamente confirmaciones de transacciones completadas. Los clubes nos pagan una tarifa mensual
            por contrato según los servicios prestados.
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Google Analytics:</strong> Análisis de uso de la aplicación
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Servicios de hosting:</strong> Almacenamiento y procesamiento de datos
          </Typography>
        </Box>

        <Box component="ul" sx={{ pl: 3, mt: 2 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cumplimiento legal:</strong> Podemos divulgar su información si es requerido por ley,
            orden judicial, proceso legal, o para proteger nuestros derechos y seguridad
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Transferencias empresariales:</strong> En caso de fusión, adquisición o venta de activos,
            su información puede transferirse al nuevo propietario
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Con su consentimiento:</strong> Podemos compartir información con terceros cuando usted nos dé
            su consentimiento explícito
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          4. Privacidad de Datos Antes del Registro
        </Typography>

        <Typography variant="body1" paragraph>
          Si un administrador de club agrega su información a la aplicación antes de que usted se registre:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            Su información permanecerá completamente <strong>privada y confidencial</strong> hasta que complete su registro
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Puede registrarse mediante un enlace de invitación o utilizando el mismo correo electrónico/teléfono
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Una vez registrado, tendrá control total sobre su información y podrá:
          </Typography>
        </Box>

        <Box component="ul" sx={{ pl: 6 }}>
          <Typography component="li" variant="body1">
            Decidir qué información es visible para otros miembros del club
          </Typography>
          <Typography component="li" variant="body1">
            Modificar su información en cualquier momento
          </Typography>
          <Typography component="li" variant="body1">
            Solicitar la eliminación completa de su cuenta y datos
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          5. Sus Derechos y Opciones
        </Typography>

        <Typography variant="body1" paragraph>
          Usted tiene los siguientes derechos sobre su información personal:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Acceso:</strong> Puede solicitar una copia de la información personal que tenemos sobre usted
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Corrección:</strong> Puede actualizar o corregir su información en cualquier momento desde su perfil
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Eliminación:</strong> Puede solicitar la eliminación de su cuenta y datos personales.
            Su cuenta se desactivará inmediatamente y toda su información se eliminará permanentemente dentro de 30 días
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Portabilidad:</strong> Puede solicitar una copia de sus datos en formato estructurado y de uso común
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Objeción:</strong> Puede oponerse al procesamiento de su información personal en ciertas circunstancias
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Retiro de consentimiento:</strong> Puede retirar su consentimiento en cualquier momento para:
          </Typography>
        </Box>

        <Box component="ul" sx={{ pl: 6 }}>
          <Typography component="li" variant="body1">
            Notificaciones push (desde configuración del dispositivo)
          </Typography>
          <Typography component="li" variant="body1">
            Acceso a ubicación (desde configuración del dispositivo)
          </Typography>
          <Typography component="li" variant="body1">
            Acceso a cámara y galería (desde configuración del dispositivo)
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          <strong>Para ejercer estos derechos:</strong>
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1">
            Acceda a la sección "Perfil" o "Configuración" en la aplicación
          </Typography>
          <Typography component="li" variant="body1">
            Para solicitudes de eliminación de cuenta, use la opción "Eliminar cuenta" en configuración
          </Typography>
          <Typography component="li" variant="body1">
            Para otras consultas, contáctenos a través del soporte en la aplicación
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          6. Seguridad de los Datos
        </Typography>

        <Typography variant="body1" paragraph>
          Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1">
            Cifrado de datos en tránsito (HTTPS/TLS)
          </Typography>
          <Typography component="li" variant="body1">
            Cifrado de datos en reposo en servidores seguros
          </Typography>
          <Typography component="li" variant="body1">
            Autenticación segura con Firebase Authentication
          </Typography>
          <Typography component="li" variant="body1">
            Controles de acceso y permisos limitados
          </Typography>
          <Typography component="li" variant="body1">
            Monitoreo regular de seguridad y actualizaciones
          </Typography>
          <Typography component="li" variant="body1">
            Cumplimiento con estándares de la industria
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro.
          Mientras nos esforzamos por proteger su información personal, no podemos garantizar su seguridad absoluta.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          7. Retención de Datos
        </Typography>

        <Typography variant="body1" paragraph>
          Conservamos su información personal solo durante el tiempo necesario para cumplir con los propósitos descritos en esta política:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Datos de cuenta activa:</strong> Mientras su cuenta esté activa y durante un período razonable después
            de la inactividad
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Datos de transacciones:</strong> Conservados durante el tiempo requerido por ley para fines fiscales
            y contables (típicamente 5-10 años)
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Datos de cuentas eliminadas:</strong> Eliminados permanentemente dentro de los 30 días posteriores
            a la solicitud de eliminación, excepto cuando la retención sea requerida por ley
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Datos agregados/anónimos:</strong> Podemos conservar datos agregados o anonimizados indefinidamente
            para análisis estadísticos
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          8. Transferencias Internacionales de Datos
        </Typography>

        <Typography variant="body1" paragraph>
          Sus datos pueden ser transferidos y procesados en servidores ubicados fuera de su país de residencia,
          incluyendo Estados Unidos y otros países donde operan nuestros proveedores de servicios.
        </Typography>

        <Typography variant="body1" paragraph>
          Cuando transferimos datos internacionalmente, implementamos salvaguardas apropiadas, incluyendo:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1">
            Cláusulas contractuales estándar aprobadas
          </Typography>
          <Typography component="li" variant="body1">
            Certificaciones de privacidad de proveedores
          </Typography>
          <Typography component="li" variant="body1">
            Medidas de seguridad técnicas y organizativas
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          9. Privacidad de Menores
        </Typography>

        <Typography variant="body1" paragraph>
          JugarHoy no está dirigida a menores de 13 años. No recopilamos conscientemente información personal de niños menores de 13 años.
        </Typography>

        <Typography variant="body1" paragraph>
          Si descubrimos que hemos recopilado información personal de un menor de 13 años sin el consentimiento
          verificable de los padres, tomaremos medidas para eliminar esa información de nuestros servidores lo antes posible.
        </Typography>

        <Typography variant="body1" paragraph>
          Si usted es padre o tutor y cree que su hijo nos ha proporcionado información personal,
          contáctenos inmediatamente para que podamos tomar las medidas apropiadas.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          10. Cookies y Tecnologías Similares
        </Typography>

        <Typography variant="body1" paragraph>
          Utilizamos cookies y tecnologías similares para mejorar su experiencia:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cookies esenciales:</strong> Necesarias para el funcionamiento básico de la aplicación
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cookies de rendimiento:</strong> Nos ayudan a entender cómo se utiliza la aplicación
            (Google Analytics)
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cookies de funcionalidad:</strong> Recuerdan sus preferencias y configuraciones
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Puede controlar las cookies a través de la configuración de su navegador o dispositivo,
          aunque esto puede afectar la funcionalidad de la aplicación.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          11. Cambios a esta Política de Privacidad
        </Typography>

        <Typography variant="body1" paragraph>
          Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas
          o por razones legales, operativas o regulatorias.
        </Typography>

        <Typography variant="body1" paragraph>
          Cuando realicemos cambios materiales, le notificaremos mediante:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1">
            Una notificación en la aplicación
          </Typography>
          <Typography component="li" variant="body1">
            Un correo electrónico a la dirección asociada con su cuenta
          </Typography>
          <Typography component="li" variant="body1">
            Actualización de la fecha "Última actualización" al inicio de esta política
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Le recomendamos revisar esta política periódicamente. Su uso continuado de la aplicación después de
          cualquier cambio constituye su aceptación de la política actualizada.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          12. Cumplimiento con Facebook
        </Typography>

        <Typography variant="body1" paragraph>
          JugarHoy cumple con los requisitos de eliminación de datos de Facebook para aplicaciones que integran servicios de Facebook:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1">
            Puede solicitar la eliminación de su cuenta desde su perfil en la aplicación
          </Typography>
          <Typography component="li" variant="body1">
            La cuenta se desactiva inmediatamente tras la solicitud
          </Typography>
          <Typography component="li" variant="body1">
            Todos los datos personales se eliminan permanentemente dentro de 30 días
          </Typography>
          <Typography component="li" variant="body1">
            Los datos de Facebook vinculados también se eliminan según las políticas de Facebook
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          13. Contacto
        </Typography>

        <Typography variant="body1" paragraph>
          Si tiene preguntas, inquietudes o solicitudes sobre esta Política de Privacidad o nuestras prácticas de datos,
          puede contactarnos:
        </Typography>

        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1">
            A través del formulario de soporte en la aplicación JugarHoy
          </Typography>
          <Typography component="li" variant="body1">
            Por correo electrónico a: soporte@jugarhoy.com
          </Typography>
          <Typography component="li" variant="body1">
            Visitando nuestro sitio web: https://jugarhoy.com
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Responderemos a su solicitud dentro de un plazo razonable (generalmente 30 días).
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
          © {new Date().getFullYear()} JugarHoy. Todos los derechos reservados.
        </Typography>
        </Paper>
      </Container>
      <SiteFooter />
    </div>
  );
};

export default PrivacyPage;
